import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Listbox } from 'primeng/listbox';

export type ShoppingCartItem = {
  id: string | number;
  label: string;
  value: any;
  disabled?: boolean;
  icon?: string;
  data?: any;
};

@Component({
  selector: 'afoone-select-shopping-cart',
  templateUrl: './select-shopping-cart.component.html',
  styleUrls: ['./select-shopping-cart.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectShoppingCartComponent),
      multi: true,
    },
  ],
})
export class SelectShoppingCartComponent
  implements ControlValueAccessor, OnChanges
{
  @ViewChild('selectedListbox') selectedListbox!: Listbox;
  @ViewChild('availableListbox') availableListbox!: Listbox;

  @Input('availableItems') initialAvailableItems:
    | ShoppingCartItem[]
    | undefined;

  @Input() leftTitle?: string;
  @Input() rightTitle?: string;
  public availableItems: ShoppingCartItem[] = [];

  selectedItems: ShoppingCartItem[] | undefined;

  public selectedItemLeft: ShoppingCartItem | undefined | null;

  public selectedItemRight: ShoppingCartItem | undefined | null;

  // ControlValueAccessor methods
  private onChange: (value: any) => void;
  private onTouched: () => void;

  writeValue(value: any): void {
    // Update the component's value
    this.selectedItems = value || [];
    this.updateAvailableItems(this.initialAvailableItems || []);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handle disabled state if necessary
  }

  isOnAvailableItems(item: ShoppingCartItem): boolean {
    return (
      this.availableItems?.some(
        (availableItem) => availableItem.id === item.id
      ) || false
    );
  }

  private scrollToTop() {
    const listboxEl = this.selectedListbox.el?.nativeElement?.querySelector(
      '.p-listbox-list'
    ) as HTMLElement;

    if (listboxEl) {
      listboxEl.scrollTop = 0; // Scroll al principio
      // listboxEl.scrollTop = listboxEl.scrollHeight; // para ir al final
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialAvailableItems']) {
      const _initialAvailableItems =
        changes['initialAvailableItems'].currentValue || [];
      this.updateAvailableItems(_initialAvailableItems);
    }
  }

  updateAvailableItems(_newItems: ShoppingCartItem[]): void {
    this.availableItems = _newItems.filter(
      (item: ShoppingCartItem) =>
        !this.selectedItems?.some((selectedItem) => selectedItem.id === item.id)
    );
  }

  /**
   * True when all currently visible (filtered) options in the left list are already selected.
   * With no filter, true only when availableItems is empty (all selected).
   */
  isAllSelected(): boolean {
    const visible =
      typeof this.availableListbox?.visibleOptions === 'function'
        ? this.availableListbox.visibleOptions()
        : null;
    if (visible && visible.length > 0) {
      const availableIds = new Set(this.availableItems.map((a) => a.id));
      return visible.every((opt: ShoppingCartItem) => !availableIds.has(opt.id));
    }
    return this.availableItems.length === 0;
  }

  findIndex(item: ShoppingCartItem): number {
    let index = -1;
    for (let i = 0; i < this.availableItems.length; i++) {
      if (item.id === this.availableItems[i].id) {
        index = i;
        break;
      }
    }
    return index;
  }

  addAll() {
    if (this.isAllSelected()) {
      // Toggle off: remove from selection only the currently visible (filtered) items
      const visible: ShoppingCartItem[] =
        typeof this.availableListbox?.visibleOptions === 'function'
          ? this.availableListbox.visibleOptions()
          : [];
      if (visible.length > 0) {
        const visibleIds = new Set(visible.map((v) => v.id));
        const toRemove = (this.selectedItems || []).filter((s) =>
          visibleIds.has(s.id)
        );
        this.selectedItems = (this.selectedItems || []).filter(
          (s) => !visibleIds.has(s.id)
        );
        this.availableItems = [...toRemove, ...this.availableItems];
      } else {
        this.availableItems = [...(this.initialAvailableItems || [])];
        this.selectedItems = this.selectedItems?.filter(
          (selectedItem) =>
            !this.availableItems?.some(
              (availableItem) => availableItem.id === selectedItem.id
            )
        );
      }
      this.onChange(this.selectedItems ?? []);
      return;
    }
    if (!this.availableItems?.length) {
      return;
    }
    // When filter is active: add only the visible (filtered) items that are still available (WIR-DSI-003-1)
    const visibleOptions: ShoppingCartItem[] =
      typeof this.availableListbox?.visibleOptions === 'function'
        ? this.availableListbox.visibleOptions()
        : this.availableItems;
    const availableIds = new Set(this.availableItems.map((a) => a.id));
    const itemsToAdd = visibleOptions.filter((opt) => availableIds.has(opt.id));
    if (itemsToAdd.length === 0) {
      return;
    }
    const addedIds = new Set(itemsToAdd.map((i) => i.id));
    this.selectedItems = [...itemsToAdd, ...(this.selectedItems || [])];
    this.availableItems = this.availableItems.filter(
      (a) => !addedIds.has(a.id)
    );
    this.onChange(this.selectedItems);
  }

  removeAll() {
    if (!this.selectedItems?.length) {
      return;
    }
    // When filter is active on the selected list: remove only the visible (filtered) items
    const visible: ShoppingCartItem[] =
      typeof this.selectedListbox?.visibleOptions === 'function'
        ? this.selectedListbox.visibleOptions()
        : [];
    if (visible.length > 0) {
      const visibleIds = new Set(visible.map((v) => v.id));
      const toRemove = this.selectedItems.filter((s) => visibleIds.has(s.id));
      this.selectedItems = this.selectedItems.filter(
        (s) => !visibleIds.has(s.id)
      );
      this.availableItems = [...toRemove, ...this.availableItems];
    } else {
      this.availableItems = [...(this.initialAvailableItems || [])];
      this.selectedItems = [];
    }
    this.onChange(this.selectedItems);
  }

  onAddClick(item: ShoppingCartItem) {
    if (!this.isOnAvailableItems(item)) {
      this.onRemoveClick(item);
      return;
    }
    if (item) {
      this.selectedItems = [item, ...(this.selectedItems || [])];
      this.availableItems = this.availableItems?.filter(
        (availableItem) => availableItem.id !== item.id
      );
      this.onChange(this.selectedItems);
      this.selectedItemLeft = null;
    }
      // Esperamos un momento para que Angular renderice el nuevo <li>
      setTimeout(() => {
        const listboxEl = this.selectedListbox.el?.nativeElement.querySelector('.p-listbox-list-wrapper') as HTMLElement;
        if (listboxEl) {
          listboxEl.scrollTop = 0; // Scroll al top
        }
      }, 50); // 50ms suele ser suficiente
  }

  onRemoveClick(item: ShoppingCartItem) {
    if (item) {
      // Ensure the item was on the initial available items
      if (this.initialAvailableItems?.some((i) => i.id === item.id)) {
        this.availableItems = [item, ...(this.availableItems || [])];
      }
      this.selectedItems = this.selectedItems?.filter(
        (selectedItem) => selectedItem.id !== item.id
      );
      this.onChange(this.selectedItems);
      this.selectedItemRight = null;
    }
  }

  addSelected() {
    if (this.selectedItemLeft) {
      this.selectedItems = [
        ...(this.selectedItems || []),
        this.selectedItemLeft,
      ];
      this.availableItems = this.availableItems?.filter(
        (item) => item.id !== this.selectedItemLeft.id
      );
      this.onChange(this.selectedItems);
      this.selectedItemLeft = null;
    }
  }

}
